import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { CHAMPIONS_LEAGUE_SETTINGS, CHAMPIONS_LEAGUE_TEAMS, ALL_CHAMPIONS_LEAGUE_GAME_DATA } from '../../../../graphql/queries';
import {
  UPDATE_CHAMPIONS_LEAGUE_PHASE_LOCKS,
  UPDATE_CHAMPIONS_LEAGUE_REAL_RESULTS,
  UPDATE_CHAMPIONS_LEAGUE_TEAMS,
  RECALCULATE_ALL_POINTS
} from '../../../../graphql/mutations';

export const useChampionsLeagueData = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Confirmation modal states
  const [phaseConfirmModal, setPhaseConfirmModal] = useState({ isOpen: false, phase: '', action: '' });
  const [resetConfirmModal, setResetConfirmModal] = useState({ isOpen: false });

  // GraphQL queries og mutations
  const { data: settingsData, loading: settingsLoading, error: settingsError } = useQuery(CHAMPIONS_LEAGUE_SETTINGS);
  const { data: teamsData, loading: teamsLoading, error: teamsError } = useQuery(CHAMPIONS_LEAGUE_TEAMS);

  const [updatePhaseLocks, { loading: updateLoading }] = useMutation(UPDATE_CHAMPIONS_LEAGUE_PHASE_LOCKS, {
    onCompleted: () => {
      setSuccess('Fase låsning opdateret!');
      setError('');
    },
    onError: (error) => {
      setError(`Fejl ved opdatering: ${error.message}`);
      setSuccess('');
    }
  });

  const [updateRealResults, { loading: updateResultsLoading }] = useMutation(UPDATE_CHAMPIONS_LEAGUE_REAL_RESULTS, {
    // Refetch game data + settings så Apollo-cache matcher DB efter gem.
    // ChampionsLeagueAdminExtended blokerer sync hvis server-svar er "tommere" end lokal state (færre vindere).
    refetchQueries: [
      { query: ALL_CHAMPIONS_LEAGUE_GAME_DATA },
      { query: CHAMPIONS_LEAGUE_SETTINGS },
    ],
    awaitRefetchQueries: false, // Don't wait for refetch - we already have updated data from response
    onCompleted: () => {
      setSuccess('Rigtige resultater opdateret! Point beregnet for alle spillere.');
      setError('');
    },
    onError: (error) => {
      setError(`Fejl ved opdatering af resultater: ${error.message}`);
      setSuccess('');
    }
  });

  const [updateTeams] = useMutation(UPDATE_CHAMPIONS_LEAGUE_TEAMS, {
    // Refetch data efter opdatering af teams
    refetchQueries: [
      { query: CHAMPIONS_LEAGUE_TEAMS },
      { query: ALL_CHAMPIONS_LEAGUE_GAME_DATA }
    ],
    awaitRefetchQueries: true,
    onCompleted: () => {
      setSuccess('Hold point og placeringer opdateret! Point genberegnet for alle spillere.');
      setError('');
    },
    onError: (error) => {
      setError(`Fejl ved opdatering af hold: ${error.message}`);
      setSuccess('');
    }
  });

  const [recalculateAllPointsMutation, { loading: recalculatePointsLoading }] = useMutation(RECALCULATE_ALL_POINTS, {
    refetchQueries: [
      { query: ALL_CHAMPIONS_LEAGUE_GAME_DATA },
      { query: CHAMPIONS_LEAGUE_SETTINGS }
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      const result = data?.recalculateAllPoints;
      if (result?.success) {
        setSuccess(result.message || 'Point genberegnet for alle spillere.');
        setError('');
      } else {
        setError('Genberegning fejlede.');
        setSuccess('');
      }
    },
    onError: (error) => {
      setError(`Fejl ved genberegning af point: ${error.message}`);
      setSuccess('');
    }
  });

  const loading = settingsLoading || teamsLoading || updateLoading || updateResultsLoading || recalculatePointsLoading;

  // Hent hold fra database eller brug fallback - bevar Team objekter med point information
  const allTeams = teamsData?.championsLeagueTeams?.teams || [];

  // Hent nuværende fase låsning fra database
  const currentPhaseLocks = useMemo(() => 
    settingsData?.championsLeagueSettings?.phaseLocks || {
      phase0: false,
      phase1: false,
      phase2: false,
      phase2Playoff: false,
      phase2Round16: false,
      phase2Quarters: false,
      phase2Semis: false,
      phase3: false
    }, [settingsData?.championsLeagueSettings?.phaseLocks]
  );

  // Hent nuværende real results fra database
  const currentRealResults = useMemo(() => 
    settingsData?.championsLeagueSettings?.realResults || {
      leagueStanding: [],
      playoffResults: [],
      roundOf16Results: [],
      quarterFinalResults: [],
      semiFinalResults: [],
      finalResult: {}
    }, [settingsData?.championsLeagueSettings?.realResults]
  );

  // Håndter GraphQL fejl
  useEffect(() => {
    if (settingsError) {
      setError(`Fejl ved hentning af indstillinger: ${settingsError.message}`);
    }
    if (teamsError) {
      setError(`Fejl ved hentning af hold: ${teamsError.message}`);
    }
  }, [settingsError, teamsError]);

  const handleTogglePhase = (phase) => {
    // Håndter både gamle numre (0, 1, 2, 3) og nye string keys ('phase2Playoff', etc.)
    const phaseKey = typeof phase === 'string' ? phase : `phase${phase}`;
    const isCurrentlyLocked = currentPhaseLocks[phaseKey];
    const action = isCurrentlyLocked ? 'låse op for' : 'låse';
    
    setPhaseConfirmModal({ isOpen: true, phase: phaseKey, action });
  };

  const confirmTogglePhase = async () => {
    if (phaseConfirmModal.phase !== '') {
      try {
        const phaseKey = phaseConfirmModal.phase;
        const isCurrentlyLocked = currentPhaseLocks[phaseKey];
        
        // Fjern __typename fra currentPhaseLocks før vi sender det
        const { __typename, ...cleanPhaseLocks } = currentPhaseLocks;
        const newPhaseLocks = {
          ...cleanPhaseLocks,
          [phaseKey]: !isCurrentlyLocked
        };
        
        await updatePhaseLocks({
          variables: {
            phaseLocks: newPhaseLocks
          }
        });
      } catch (error) {
        console.error('Error updating phase locks:', error);
      }
    }
    setPhaseConfirmModal({ isOpen: false, phase: '', action: '' });
  };

  const handleResetGame = () => {
    setResetConfirmModal({ isOpen: true });
  };

  const confirmResetGame = () => {
    // For nu, nulstil kun localStorage - backend reset kan tilføjes senere
    localStorage.removeItem('championsLeagueGame');
    localStorage.removeItem('championsLeagueRealResults');
    localStorage.removeItem('championsLeagueTeamPoints');
    localStorage.removeItem('championsLeagueMatches');
    setSuccess('Champions League spil er nulstillet!');
    setResetConfirmModal({ isOpen: false });
  };

  const handleRecalculateAllPoints = async () => {
    await recalculateAllPointsMutation();
  };

  return {
    // State
    error,
    success,
    isLoading,
    setIsLoading,
    setError,
    setSuccess,
    
    // Data
    allTeams,
    currentPhaseLocks,
    currentRealResults,
    loading,
    
    // Mutations
    updatePhaseLocks,
    updateRealResults,
    updateTeams,
    recalculatePointsLoading,
    
    // Handlers
    handleTogglePhase,
    handleResetGame,
    handleRecalculateAllPoints,
    confirmTogglePhase,
    confirmResetGame,
    
    // Confirmation modals
    phaseConfirmModal,
    resetConfirmModal,
    setPhaseConfirmModal,
    setResetConfirmModal
  };
};
